import { closeDialog, ensureSwiper, events, fetchSection, focusQuietly, openDialog, parseHTML, prefersReducedMotion } from './theme.js';

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.abort = null;
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onChange = (e) => {
    const input = e.target;
    if (!input.closest('variant-picker')) return;
    const picker = input.closest('variant-picker');
    const selected = Array.from(picker.querySelectorAll('input[type="radio"]:checked, select option:checked'))
      .map((el) => el.dataset.optionValueId)
      .filter(Boolean);
    const optionUrl = (input.tagName === 'SELECT' ? input.selectedOptions[0] : input).dataset.productUrl;
    const productUrl = optionUrl || this.dataset.productUrl;
    const productChanged = optionUrl && optionUrl !== this.dataset.productUrl;

    const fromHere = e.isTrusted && this.contains(document.activeElement);
    this.render(productUrl, selected, productChanged, fromHere ? input : null);
  };

  whenReady() {
    return this.pending || Promise.resolve();
  }

  render(url, optionValues, productChanged, trigger) {
    const run = this.renderNow(url, optionValues, productChanged, trigger);
    const pending = run.finally(() => {
      if (this.pending === pending) this.pending = null;
    });
    this.pending = pending;
    return pending;
  }

  async renderNow(url, optionValues, productChanged, trigger) {
    this.abort?.abort();
    this.abort = new AbortController();
    const u = new URL(url, window.location.origin);
    if (optionValues.length) u.searchParams.set('option_values', optionValues.join(','));
    this.setAttribute('aria-busy', 'true');
    let doc;
    try {
      doc = await fetchSection(u.pathname + u.search, this.sectionId, { signal: this.abort.signal });
    } catch (err) {
      if (err.name !== 'AbortError') this.removeAttribute('aria-busy');
      return;
    }
    this.removeAttribute('aria-busy');
    const incoming = doc.querySelector(`product-info[data-section-id="${this.sectionId}"]`);
    if (!incoming) return;
    this.updateOutside(doc, productChanged);
    const focusKey = trigger?.id;

    if (productChanged) {

      this.replaceChildren(...Array.from(incoming.childNodes).map((n) => document.importNode(n, true)));
      this.dataset.productUrl = incoming.dataset.productUrl;
    } else {
      incoming.querySelectorAll('[data-update]').forEach((fresh) => {
        const key = fresh.dataset.update;
        this.querySelectorAll(`[data-update="${key}"]`).forEach((current) => {
          if (current.closest('product-info') !== this) return;
          current.replaceWith(document.importNode(fresh, true));
        });
      });
    }

    const variantId = incoming.dataset.variantId;
    this.dataset.variantId = variantId || '';
    if (focusKey) {
      const restore = document.getElementById(focusKey);
      if (restore && this.contains(restore)) focusQuietly(restore);
    }

    if (this.hasAttribute('data-update-url')) {
      const next = new URL(incoming.dataset.productUrl || u.pathname, window.location.origin);
      if (variantId) next.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', next.pathname + next.search);
    }

    const mediaId = incoming.dataset.featuredMediaId;
    if (mediaId) this.querySelector('media-gallery')?.goTo(mediaId, false);

    events.emit('variant:changed', { sectionId: this.sectionId, variantId, productUrl: incoming.dataset.productUrl });
  }

  updateOutside(doc, productChanged) {
    const scope = this.closest('.shopify-section') || this.parentElement;
    if (!scope) return;
    if (productChanged) {

      const bar = doc.querySelector('sticky-buy-bar');
      const current = scope.querySelector('sticky-buy-bar');
      if (bar && current) current.replaceWith(document.importNode(bar, true));
      return;
    }
    doc.querySelectorAll('[data-update]').forEach((fresh) => {
      if (fresh.closest('product-info')) return;
      scope.querySelectorAll(`[data-update="${fresh.dataset.update}"]`).forEach((current) => {
        if (!current.closest('product-info')) current.replaceWith(document.importNode(fresh, true));
      });
    });
  }
}
customElements.define('product-info', ProductInfo);

class VariantPicker extends HTMLElement {}
customElements.define('variant-picker', VariantPicker);

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = this.querySelector('[data-gallery-thumbs]');
    this.dots = this.querySelector('[data-gallery-dots]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.thumbs?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-media-target]');
      if (b) this.goTo(b.dataset.mediaTarget);
    });
    this.dots?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-media-target]');
      if (b) this.goTo(b.dataset.mediaTarget);
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.initZoom();
    this.sync();
    this.initSwiper().then(() => this.sync());
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.zoomObserver?.disconnect();
  }

  // Full-screen view: every media stacked full width, opened by clicking a gallery image.
  initZoom() {
    const zoom = this.querySelector('[data-gallery-zoom]');
    const scroller = zoom?.querySelector('[data-zoom-scroller]');
    if (!scroller || !this.track) return;
    const zoomItems = Array.from(scroller.querySelectorAll('[data-zoom-id]'));
    const thumbs = Array.from(zoom.querySelectorAll('[data-zoom-target]'));

    const loadModel = (host, template) => {
      host.append(template.content.cloneNode(true));
      window.Shopify?.loadFeatures?.([{ name: 'model-viewer-ui', version: '1.0', onLoad: (errors) => {
        const viewer = host.querySelector('model-viewer');
        if (errors || !viewer || !window.Shopify.ModelViewerUI) return;
        host.modelViewerUI = new window.Shopify.ModelViewerUI(viewer);
        host.modelViewerUI.play?.();
      } }]);
    };

    // Only the current media plays: hosted videos start muted, external ones get their iframe on demand.
    const play = (id) => {
      zoomItems.forEach((el) => {
        const current = zoom.open && el.dataset.zoomId === id;
        const video = el.querySelector('video');
        if (video) current ? video.play()?.catch(() => {}) : video.pause();
        const embed = el.querySelector('template');
        if (!embed) return;
        // 3D models load once and then stay; leaving them only pauses the viewer.
        const modelHost = el.querySelector('[data-zoom-model]');
        if (modelHost) {
          if (current && !modelHost.querySelector('model-viewer')) loadModel(modelHost, embed);
          else if (!current) modelHost.modelViewerUI?.pause?.();
          return;
        }
        const frame = el.querySelector('iframe');
        if (current && !frame) embed.after(embed.content.cloneNode(true));
        else if (!current) frame?.remove();
      });
    };
    const mark = (id) => {
      thumbs.forEach((t) => {
        const active = t.dataset.zoomTarget === id;
        t.setAttribute('aria-current', String(active));
        if (active) t.scrollIntoView({ block: 'nearest' });
      });
      play(id);
    };
    const show = (id, behavior = 'auto') => {
      const target = zoomItems.find((el) => el.dataset.zoomId === id);
      if (!target) return;
      scroller.scrollTo({ top: target.offsetTop, behavior });
      mark(id);
    };

    this.track.addEventListener('click', (e) => {
      const item = e.target.closest('[data-media-id]');
      if (!item || e.target.closest('deferred-media, button, a')) return;
      openDialog(zoom);
      show(item.dataset.mediaId);
    });

    zoom.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-zoom-target]');
      if (thumb) {
        show(thumb.dataset.zoomTarget, prefersReducedMotion() ? 'auto' : 'smooth');
      } else if (e.target.matches('.gallery-zoom__item > img')) {
        closeDialog(zoom);
      }
    });

    zoom.addEventListener('close', () => play(null));

    // Whichever media crosses the middle of the viewport is the current one.
    this.zoomObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) mark(entry.target.dataset.zoomId);
      });
    }, { root: scroller, rootMargin: '-50% 0px -50% 0px' });
    zoomItems.forEach((el) => this.zoomObserver.observe(el));
  }

  get items() {
    return Array.from(this.track?.querySelectorAll('[data-media-id]') || []);
  }

  async initSwiper() {
    if (!this.track || this.items.length < 1) return;
    const Swiper = await ensureSwiper();
    if (!Swiper || !this.isConnected || this.swiper) return;

    const viewport = document.createElement('div');
    viewport.className = 'swiper gallery__viewport';
    this.track.replaceWith(viewport);
    viewport.append(this.track);

    this.track.classList.add('swiper-wrapper');
    this.items.forEach((el) => el.classList.add('swiper-slide'));

    // The editorial layout lays every media out in the product grid, so it never slides on desktop.
    const horizontalOnDesktop = this.classList.contains('gallery--carousel') && !this.closest('.product--editorial');
    this.swiper = new Swiper(viewport, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      speed: prefersReducedMotion() ? 0 : 350,
      grabCursor: true,
      threshold: 4,
      watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true },
      // The track is a <ul role="list">, so slides must keep the listitem role Swiper would replace.
      a11y: { enabled: true, slideRole: /^(UL|OL)$/.test(this.track.tagName) ? 'listitem' : 'group' },
      breakpoints: { 990: { enabled: horizontalOnDesktop, spaceBetween: 0 } },
    });

    this.closest('dialog')?.addEventListener('dialog:open', () => this.swiper?.update());
    this.swiper.on('slideChange', () => {
      const item = this.items[this.swiper.activeIndex];
      if (item) this.setActive(item.dataset.mediaId);
      this.updateCounter(this.swiper.activeIndex);
      this.querySelectorAll('deferred-media[data-model]').forEach((m) => {
        if (!item?.contains(m)) m.modelViewerUI?.pause?.();
      });
    });
  }

  current() {
    return this.swiper?.enabled ? this.swiper.activeIndex : 0;
  }

  step(dir) {
    if (this.swiper?.enabled) {
      dir > 0 ? this.swiper.slideNext() : this.swiper.slidePrev();
      return;
    }
    const i = Math.max(0, Math.min(this.items.length - 1, this.current() + dir));
    this.scrollTo(this.items[i]);
  }

  goTo(mediaId, scroll = true) {
    const item = this.items.find((el) => el.dataset.mediaId === String(mediaId));
    if (!item) return;
    if (this.swiper?.enabled) {
      this.swiper.slideTo(this.items.indexOf(item));
      this.setActive(item.dataset.mediaId);
      return;
    }
    if (scroll) {
      this.scrollTo(item);
      return;
    }
    this.setActive(item.dataset.mediaId);
  }

  scrollTo(item) {
    if (!item) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const r = item.getBoundingClientRect();
    if (r.top < 0 || r.top > window.innerHeight) item.scrollIntoView({ behavior, block: 'start' });
    this.setActive(item.dataset.mediaId);
  }

  sync() {
    const i = this.current();
    const item = this.items[i];
    if (item) this.setActive(item.dataset.mediaId);
    this.updateCounter(i);
  }

  updateCounter(i) {
    if (this.counter) this.counter.textContent = `${i + 1} / ${this.items.length}`;
  }

  setActive(id) {
    this.items.forEach((el) => {
      const active = el.dataset.mediaId === String(id);
      el.toggleAttribute('data-active', active);
      if (!active) el.querySelectorAll('video').forEach((v) => v.pause());
    });
    this.thumbs?.querySelectorAll('[data-media-target]').forEach((t) => {
      t.setAttribute('aria-current', String(t.dataset.mediaTarget === String(id)));
    });
    this.dots?.querySelectorAll('[data-media-target]').forEach((d) => {
      const active = d.dataset.mediaTarget === String(id);
      d.setAttribute('aria-current', String(active));
      d.classList.toggle('is-active', active);
    });
  }

}
customElements.define('media-gallery', MediaGallery);

class PickupAvailability extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.variantId) return;
    const url = `${window.Atelier.routes.root_url.replace(/\/$/, '')}/variants/${this.dataset.variantId}/?section_id=pickup-availability`;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = parseHTML(html);
        const content = doc.querySelector('[data-pickup-content]');
        if (!content || !content.children.length) {
          this.hidden = true;
          return;
        }
        this.replaceChildren(...Array.from(content.childNodes).map((n) => document.importNode(n, true)));
        this.hidden = false;
      })
      .catch(() => (this.hidden = true));
  }
}
customElements.define('pickup-availability', PickupAvailability);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    const load = () => {
      const url = `${this.dataset.url}&section_id=${this.dataset.sectionId}`;
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const fresh = doc.querySelector(`product-recommendations[data-key="${this.dataset.key}"]`);
          this.dataset.loaded = 'true';
          if (!fresh || !fresh.querySelector('[data-rec-item]')) {
            this.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
            this.hidden = true;
            return;
          }
          this.replaceChildren(...Array.from(fresh.childNodes).map((n) => document.importNode(n, true)));
        })
        .catch(() => (this.hidden = true));
    };
    this.io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.io.disconnect();
      load();
    }, { rootMargin: '400px 0px' });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector(this.dataset.watch);
    if (!target) return;
    this.io = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', passed);
      this.toggleAttribute('inert', !passed);
      this.schedule();
    });
    this.io.observe(target);
    this.ro = new ResizeObserver(() => this.schedule());
    this.ro.observe(this);
    this.mo = new MutationObserver(() => this.schedule());
    this.mo.observe(this, { childList: true, subtree: true });
    this.addEventListener('scroll', this.schedule, { capture: true, passive: true });
    this.sync();
    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-sticky-add]')) return;
      const form = document.getElementById(this.dataset.form);
      form?.requestSubmit();
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-sticky-option]')) this.pickOption(e.target);
      this.schedule();
    });
  }

  async pickOption(control) {
    const info = document.querySelector(this.dataset.watch) || document.querySelector('product-info');
    const picker = (info || document).querySelector('variant-picker');
    if (!picker) return;
    const group = picker.querySelectorAll('.variant-option')[Number(control.dataset.stickyOption) - 1];
    if (!group) return;
    const value = control.value;
    const focusKey = control.id;
    const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);

    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const dropdown = group.querySelector('select');
      if (!dropdown) return;
      dropdown.value = value;
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));
    }

    await info?.whenReady?.();
    const restore = focusKey && document.getElementById(focusKey);
    if (restore && restore !== control && this.contains(restore)) focusQuietly(restore);
  }

  vars = {};

  // Scroll, resize and mutation can all land in the same frame; collapse them into one pass.
  schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.sync();
    });
  };

  // Writing a custom property on <html> invalidates layout for the whole document, so skip
  // the write when the value hasn't moved.
  setVar(name, value) {
    if (this.vars[name] === value) return;
    this.vars[name] = value;
    document.documentElement.style.setProperty(name, value);
  }

  /*
    One pass, every read before any write: measuring a swatch row after touching <html> would
    force the browser to redo layout synchronously, once per row.
  */
  sync() {
    const offset = this.hasAttribute('data-offset');
    const top = this.classList.contains('sticky-buy--top');
    const shown = this.hasAttribute('data-visible');
    const rows = this.querySelectorAll('.sticky-buy__swatches');

    const height = offset || top ? this.offsetHeight : 0;
    const overflow = Array.from(rows, (row) => row.scrollWidth - row.clientWidth - row.scrollLeft > 1);

    if (offset) this.setVar('--sticky-buy-h', `${height}px`);
    if (top) {
      this.setVar('--sticky-buy-top-h', `${shown ? height : 0}px`);
      document.documentElement.classList.toggle('sticky-buy-active', shown);
    }
    rows.forEach((row, i) => row.toggleAttribute('data-overflow', overflow[i]));
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    // The properties below get removed, so drop the cache or a re-connect would skip rewriting them.
    this.vars = {};
    this.io?.disconnect();
    this.ro?.disconnect();
    this.mo?.disconnect();
    if (!document.querySelector('sticky-buy-bar[data-offset]')) {
      document.documentElement.style.removeProperty('--sticky-buy-h');
    }
    if (!document.querySelector('sticky-buy-bar.sticky-buy--top')) {
      document.documentElement.style.removeProperty('--sticky-buy-top-h');
      document.documentElement.classList.remove('sticky-buy-active');
    }
  }
}
customElements.define('sticky-buy-bar', StickyBuyBar);
